import { takeEvery, select, put, cancel, call, delay, fork } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { verificationWorkFlow, getWorkflowTask, updateWorkflowTask, updateWaitingStartMsg } from '../saga';
import { START_D2D_EXTRACTION } from './actionTypes';
import D2DChannelConstructor from '../../../../../services/Rocky/D2D/constructors/channelConstructor';
import { simulationD2DPromise } from '../../../../../services/Rocky/D2D';
import { changeVerificationList, getVerificationLog, updateCurrentSimulationInfo, updateSimulationReducer } from '../action';
import { API_RES_MESSAGE } from '../../../../../constants/returnCode';
import { autoGetVerificationList } from '../../project/action';
import { checkVerificationStatus, getVerificationWorkFlow } from '../../../../../services/workflow/workflow';
import { VERIFY_SUCCESS } from '../../../../../constants/verificationStatus';
import { D2D_CHANNEL } from '../../../../../constants/treeConstants';
import { getD2DComplianceErrorCheck } from '../../../errorCheck/ErrorCheck';
import { updateErrorCheckList } from '../../rocky/action';
import { getVerificationConfigPromise } from '../../../../../services/Rocky';
import { AURORA_NGSPICE } from '../../../../../constants/resolution';
import { updatePinToPinConfig } from '../../../../../services/Sierra';

let workflowTask = getWorkflowTask();

function* _startModeling(action) {
  let { channelId, options } = action;

  yield put(changeVerificationList([]));
  if (!channelId) {
    return;
  }

  //cancel prev workflow task
  if (workflowTask) {
    yield cancel(workflowTask);
    updateWorkflowTask(null);
  }

  let reducer = yield select();
  let simulationReducer = reducer.RockyReducer.simulationReducer;
  let newSimulationReducer = simulationReducer ? JSON.parse(JSON.stringify(simulationReducer)) : {};
  const info = D2DChannelConstructor.get(channelId);
  let errorCheck = null;
  const complianceCheck = options.includes('compliance');
  errorCheck = yield call(d2dComplianceErrorCheck, { verificationId: info ? info.verificationId : null, channelId, complianceCheck });

  if (!info || !info.verificationId || errorCheck) {
    return;
  }
  const { verificationId } = info;
  yield call(checkD2DConfig, { verificationId });

  delete newSimulationReducer[verificationId];
  const time = new Date();
  time.setUTCSeconds(time.getUTCSeconds());
  let waitingTime = dayjs(time.toUTCString()).format('HH:mm:ss');

  newSimulationReducer[verificationId] = {
    startMsg: `==> ${waitingTime} Checking setup...`,
    cancelDisabled: true,
    running: true,
  }

  yield put(updateSimulationReducer(newSimulationReducer));

  let waitingTask = null;
  const { project: { currentProjectId } } = reducer.RockyReducer;
  try {
    waitingTask = yield fork(updateWaitingStartMsg, { verificationIds: [verificationId], _simulation: newSimulationReducer })
    yield call(simulationD2DPromise, { d2dChannelId: channelId, options });
    yield cancel(waitingTask);
  } catch (error) {
    if (waitingTask) { yield cancel(waitingTask); }
    console.error(error)

    // update newSimulationReducer
    newSimulationReducer[verificationId] = {};
    let msg = ""
    if (error && error.code === API_RES_MESSAGE) {
      msg = `==> ${error.msg}`;
      // get log
      yield put(getVerificationLog(verificationId));
      newSimulationReducer[verificationId].simulationMsg = msg;
    } else {
      msg = `==> ${error}`;
      newSimulationReducer[verificationId].endMsg = msg;
    }

    //update simulation reducer
    yield put(updateSimulationReducer(newSimulationReducer));
    //update tree verification status
    yield put(autoGetVerificationList({ projectId: currentProjectId, channelId: channelId }));

    yield delay(1000);
    yield put(updateCurrentSimulationInfo({ currentVerificationId: verificationId, item: "startMsg", info: null }));
    yield put(updateCurrentSimulationInfo({ currentVerificationId: verificationId, item: "progress", info: -1 }));
    yield put(changeVerificationList([]));

    const promise = yield call(checkVerificationStatus, verificationId);
    let resultExist = false;

    if (promise && promise.status) {
      if (promise.status === VERIFY_SUCCESS) {
        resultExist = true;
      }
    };

    yield put(updateCurrentSimulationInfo({ currentVerificationId: verificationId, item: "resultExist", info: resultExist }));

    return;
  }

  // fake progress
  // id: workflowId
  yield put(autoGetVerificationList({ projectId: currentProjectId, channelId }));

  //update cancel disable status
  let newReducer = yield select();
  let _simulationReducer = newReducer.RockyReducer.simulationReducer;

  if (_simulationReducer[verificationId]) {
    _simulationReducer[verificationId].cancelDisabled = false;
  }

  yield put(updateSimulationReducer(_simulationReducer));
  const verificationStatus = yield call(getVerificationWorkFlow, verificationId);
  if (verificationStatus && verificationStatus.length > 0) {
    if (workflowTask) {
      yield cancel(workflowTask);
    };

    const _workflowTask = yield fork(verificationWorkFlow, { verificationId, verificationWork: verificationStatus, dataType: D2D_CHANNEL });
    updateWorkflowTask(_workflowTask);
    return;
  };
}

function* checkD2DConfig({ verificationId }) {
  let _config = yield call(getVerificationConfigPromise, { verificationId })
  const configContent = _config && _config.configContent ? _config.configContent : {};
  let compliance = configContent.compliance || {};
  if (compliance && !compliance.simulate) {
    compliance.simulate = AURORA_NGSPICE;
    compliance.ngspiceConfig = { ngspiceParallelism: 4 };
    yield call(updatePinToPinConfig, {
      projectId: _config && _config.projectId ? _config.projectId : '',
      verificationId: _config && _config.verificationId ? _config.verificationId : '',
      config: {
        ...configContent, compliance, clock: '3.2G',
        cycles: '10',
        timeStep: '20p',
      },
      id: _config.id
    })
  }
}

function* d2dSimulationErrorCheck({ verificationId, channelId }) {
  try {
    let { RockyReducer: { simulationReducer, D2D: { id, content } } } = yield select();
    let _content = {};
    if (channelId === id) {
      _content = JSON.parse(JSON.stringify(content));
    } else {
      _content = yield call(D2DChannelConstructor.getChannelContentPromise(channelId))
    }

  } catch (error) {
    console.error(error)
  }
}

function* d2dComplianceErrorCheck({ verificationId, channelId, complianceCheck }) {
  try {
    let { RockyReducer: { rocky: { infoErrorCheck }, D2D: { id, content, verificationId } } } = yield select();
    let _content = {};
    if (channelId === id) {
      _content = JSON.parse(JSON.stringify(content));
    } else {
      _content = yield call(D2DChannelConstructor.getChannelContentPromise(channelId))
    }
    const _components = _content && _content.interposer && _content.interposer.Components ? _content.interposer.Components : [];
    let errorCheck = complianceCheck ? getD2DComplianceErrorCheck(_components) : null;
    const errorIndex = infoErrorCheck.findIndex(error => error.verificationId === verificationId);
    if (errorIndex > -1) {
      infoErrorCheck[errorIndex].errorCheck = errorCheck ? { ...errorCheck } : null;
    } else if (errorCheck) {
      infoErrorCheck.push({
        verificationId: verificationId,
        errorCheck: errorCheck
      })
    }
    yield put(updateErrorCheckList(infoErrorCheck));

    return errorCheck;

  } catch (error) {
    console.error(error)
  }
}

function* D2DSimulationSaga() {
  yield takeEvery(START_D2D_EXTRACTION, _startModeling);
}

export default D2DSimulationSaga;